import { Icon } from '@iconify/react'
import {
  formatCompactDate,
  formatCurrency,
  formatNumber,
  type DashboardInsights,
} from '@/lib/campaign-dashboard'
import type {
  CampaignDashboardCampaign,
  CampaignDashboardSummary,
} from '@/types/campaign-report'
import styles from './CampaignDashboard.module.css'

interface CampaignDashboardHealthProps {
  campaign: CampaignDashboardCampaign
  summary: CampaignDashboardSummary
  insights: DashboardInsights
}

type HealthMetric = {
  label: string
  value: string
  secondaryValue?: string
  hint: string
}

function getProjectedSelloutLabel(value: string | null): string {
  if (!value) return 'Sem projecao'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Sem projecao'

  const currentYear = new Date().getFullYear()
  if (date.getFullYear() > currentYear + 3) return 'Ritmo insuficiente'

  return formatCompactDate(value)
}

export function CampaignDashboardHealth({
  campaign,
  summary,
  insights,
}: CampaignDashboardHealthProps) {
  const metrics: HealthMetric[] = [
    {
      label: 'Receita por comprador',
      value: formatCurrency(insights.revenuePerDonor),
      hint: 'Media de receita por comprador do periodo',
    },
    {
      label: 'Receita por numero',
      value: formatCurrency(insights.revenuePerNumber),
      hint: 'Quanto cada numero vendido gerou em media',
    },
    {
      label: 'Media diaria de receita',
      value: formatCurrency(insights.averageRevenuePerDay),
      hint: 'Receita media por dia no periodo',
    },
    {
      label: 'Media diaria de pagamentos',
      value: formatNumber(insights.averagePaymentsPerDay),
      hint: 'Pagamentos medios por dia',
    },
    {
      label: 'Media diaria de numeros',
      value: formatNumber(insights.averageNumbersPerDay),
      hint: 'Numeros vendidos por dia',
    },
    {
      label: 'Dias sem venda',
      value: formatNumber(insights.daysWithoutSales),
      hint: 'Dias do periodo sem pagamentos',
    },
    {
      label: 'Melhor dia em receita',
      value: insights.bestRevenueDay ? formatCompactDate(insights.bestRevenueDay.date) : '-',
      secondaryValue: insights.bestRevenueDay
        ? formatCurrency(insights.bestRevenueDay.amount)
        : undefined,
      hint: 'Maior receita diaria',
    },
    {
      label: 'Pico de pagamentos',
      value: insights.peakPaymentsDay ? formatCompactDate(insights.peakPaymentsDay.date) : '-',
      secondaryValue: insights.peakPaymentsDay
        ? `${formatNumber(insights.peakPaymentsDay.payments_count)} pagamentos`
        : undefined,
      hint: 'Maior volume de pagamentos diarios',
    },
    {
      label: 'Projecao de esgotamento',
      value: getProjectedSelloutLabel(insights.projectedSelloutDate),
      hint: 'Estimativa simples mantendo o ritmo atual',
    },
  ]

  return (
    <section className={styles.sectionCard}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>Saude da campanha</h2>
          <p className={styles.sectionSubtitle}>
            Indicadores derivados no frontend para leitura operacional do ritmo da campanha.
          </p>
        </div>
      </div>

      <div className={styles.healthMeta}>
        <span className={styles.metaPill}>
          <Icon icon="mdi:numeric" />
          Vendidos: {formatNumber(insights.soldNumbers)}
        </span>
        <span className={styles.metaPill}>
          <Icon icon="mdi:counter" />
          Restantes:{' '}
          {insights.remainingNumbers !== null
            ? formatNumber(insights.remainingNumbers)
            : 'Sem base'}
        </span>
      </div>

      <div className={styles.progressSection}>
        <div className={styles.progressWrap}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{
                width: `${Math.max(0, Math.min(summary.sell_through_percent ?? 0, 100))}%`,
              }}
            />
          </div>
        </div>
        <span className={styles.progressText}>
          {summary.sell_through_percent !== null
            ? `${summary.sell_through_percent}% do total configurado foi vendido`
            : 'Nao foi possivel calcular a ocupacao desta campanha.'}
        </span>
      </div>

      <div className={styles.healthGrid}>
        {metrics.map((metric) => (
          <article key={metric.label} className={styles.healthCard}>
            <p className={styles.kpiLabel}>{metric.label}</p>
            <div className={styles.healthValueWrap}>
              <p className={styles.healthValue}>{metric.value}</p>
              {metric.secondaryValue && (
                <p className={styles.healthSecondaryValue}>{metric.secondaryValue}</p>
              )}
            </div>
            <p className={styles.healthHint}>{metric.hint}</p>
          </article>
        ))}
      </div>

      {insights.alerts.length > 0 && (
        <div className={styles.alertsList}>
          {insights.alerts.map((alert) => (
            <div key={alert} className={styles.alertItem}>
              <Icon icon="mdi:alert-outline" />
              <span>{alert}</span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.healthFooter}>
        {campaign.status === 'active' && (
          <span className={styles.badgeInfo}>
            <Icon icon="mdi:play-circle-outline" />
            Campanha em operacao
          </span>
        )}
      </div>
    </section>
  )
}
